import { useCallback, useEffect, useState } from 'react';
import {
  getTypesQcm,
  rattacherFormationsQcm,
  lireFormulaireTally,
  getChampsCommunsQcm,
  createChampCommunQcm,
  updateChampCommunQcm,
  deleteChampCommunQcm,
  getSectionsQcm,
  createSectionQcm,
  updateSectionQcm,
  deleteSectionQcm,
  getVersionsQcm,
  createVersionQcm,
  updateVersionQcm,
  dupliquerVersionQcm,
  deleteVersionQcm,
  getQuestionsQcm,
  createQuestionQcm,
  updateQuestionQcm,
  deleteQuestionQcm,
  getPassagesQcm,
  getPassageQcm,
  deletePassageQcm,
  analyserImportQcm,
  enregistrerImportQcm,
} from '../services/qcmService';
import ConfirmationSuppression from '../components/ConfirmationSuppression';
import ImportTexteModal from '../components/ImportTexteModal';
import RemplirParTexte from '../components/RemplirParTexte';
import { getFormations } from '../services/formationService';
import PanneauApercu from './PanneauApercu';
import { formatDateTime } from './devisUtils';
import { FlashToast } from '../components/Toasts';
import '../assets/styles/Devis.css';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/Listes.css';
import '../assets/styles/Clients.css';
import '../assets/styles/Qcm.css';

/**
 * QCM (migrations 087 et 096).
 *
 * Une banque de questions par type de QCM — recrutement (Clients,
 * Formateurs) et une évaluation par formation — chacune avec ses versions et
 * ses questions à barème gradué par option ; les sections pondérées sont
 * communes à tous. Le score se calcule côté serveur à la soumission d'un
 * passage : cette page gère la banque (CRUD) et lit les résultats.
 */
/* Deux onglets : les évaluations (un choix d'évaluation dans l'onglet), et
   les sections — communes à toutes (migration 088), donc pas rangées sous un
   type. Les QCM de recrutement (clients, formateurs) ne sont plus proposés :
   leurs données restent en base. */
const ONGLETS = [
  { cle: 'evaluation', label: 'Évaluations' },
  { cle: 'sections', label: 'Sections & pondération' },
];

const SOUS_VUES = [
  { cle: 'questions', label: 'Questions' },
  { cle: 'passages', label: 'Passages' },
];

const TYPES_QUESTION = {
  qcm: 'Choix unique',
  qcm_multiple: 'Choix multiple',
};

/** Pourcentage lisible : 0.15 -> « 15 % ». */
const pourcent = (poids) => `${Math.round(Number(poids) * 1000) / 10} %`;

/* Colonnes de la relecture d'un import : l'IA a choisi l'axe de chaque
   question (section), tout reste corrigeable avant d'enregistrer. */
const COLONNES_IMPORT_QCM = [
  { cle: 'enonce', label: 'Énoncé', type: 'textarea', obligatoire: true, zone: 'titre', grand: true },
  { cle: 'section', label: 'Section (axe)', obligatoire: true, resume: true, largeur: 150 },
  {
    cle: 'type',
    label: 'Type',
    type: 'select',
    resume: true,
    options: [
      { value: 'qcm', label: 'Choix unique' },
      { value: 'qcm_multiple', label: 'Choix multiple' },
    ],
    largeur: 140,
  },
  { cle: 'tempsLimiteS', label: 'Temps (s)', type: 'number', min: 5, largeur: 80 },
  { cle: 'ordre', label: 'Ordre', type: 'number', largeur: 70 },
  {
    cle: 'optionsTexte',
    label: 'Options — une par ligne : « points | libellé », * devant les bonnes d’un choix multiple',
    type: 'textarea',
    obligatoire: true,
  },
];

function Qcm() {
  const [onglet, setOnglet] = useState('evaluation');
  const [vue, setVue] = useState('questions');
  // Les types lus en base : les évaluations se choisissent dans leur onglet.
  const [types, setTypes] = useState([]);
  const [evaluation, setEvaluation] = useState(null);

  useEffect(() => {
    getTypesQcm()
      .then((res) => {
        const liste = res.data || [];
        setTypes(liste);
        setEvaluation((courant) => courant || liste.find((t) => t.famille === 'evaluation')?.code || null);
      })
      .catch(() => setTypes([]));
  }, []);

  const evaluations = types.filter((t) => t.famille === 'evaluation');

  /** Un type revient du serveur avec ses formations à jour. */
  function majType(type) {
    setTypes((liste) => liste.map((t) => (t.code === type.code ? { ...t, ...type } : t)));
  }
  // Le type dont la banque est affichée.
  const typeActif = onglet === 'evaluation' ? evaluation : onglet;
  const infoType = types.find((t) => t.code === typeActif) || null;
  const libelleType = infoType
    ? infoType.famille === 'evaluation'
      ? `Évaluation : ${infoType.libelle}`
      : `QCM ${infoType.libelle.toLowerCase()}`
    : 'QCM';

  return (
    <div className="devis-page qcm-page">
      <div className="devis-header">
        <div>
          <h1 className="devis-title">QCM</h1>
        </div>
      </div>

      <nav className="clients-onglets">
        {ONGLETS.map((t) => (
          <button
            key={t.cle}
            type="button"
            className={'clients-onglet' + (onglet === t.cle ? ' clients-onglet-actif' : '')}
            onClick={() => setOnglet(t.cle)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {onglet === 'evaluation' && (
        <div className="qcm-types" role="group" aria-label="Évaluation à gérer">
          {evaluations.map((t) => (
            <button
              key={t.code}
              type="button"
              className={'qcm-type' + (evaluation === t.code ? ' qcm-type-actif' : '')}
              onClick={() => setEvaluation(t.code)}
            >
              {t.libelle}
            </button>
          ))}
          {evaluations.length === 0 && (
            <span className="qcm-types-vide">Aucune évaluation définie.</span>
          )}
        </div>
      )}

      {onglet === 'evaluation' && <QuestionsCommunes />}

      {onglet === 'evaluation' && infoType && (
        <FormationsRattachees key={infoType.code} type={infoType} onChange={majType} />
      )}

      {onglet === 'sections' ? (
        <OngletSections />
      ) : !typeActif ? null : (
        <>
          <div className="qcm-sousnav">
            {SOUS_VUES.map((v) => (
              <button
                key={v.cle}
                type="button"
                className={'qcm-sousnav-bouton' + (vue === v.cle ? ' qcm-sousnav-actif' : '')}
                onClick={() => setVue(v.cle)}
              >
                {v.label}
              </button>
            ))}
          </div>

          {/* `key` : changer de QCM remonte le composant à zéro, chacun
              repart de ses propres versions. */}
          {vue === 'questions' && (
            <OngletQuestions key={typeActif} type={typeActif} libelleType={libelleType} />
          )}
          {vue === 'passages' && <OngletPassages key={typeActif} type={typeActif} />}
        </>
      )}
    </div>
  );
}

/* ================================================================== */
/* Questions communes du début de chaque évaluation                    */
/* ================================================================== */

const NATURES_CHAMP = { texte: 'Texte', email: 'E-mail' };

/**
 * Les questions d'identification posées au début de CHAQUE évaluation, non
 * notées : adresse e-mail, NOM Prénom, nom du formateur, nom de l'entreprise
 * (migration 098). Une seule liste pour toutes les évaluations ; l'import d'un
 * formulaire Tally y ajoute celles qui manquent.
 */
function QuestionsCommunes() {
  const [champs, setChamps] = useState([]);
  const [ouvert, setOuvert] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [libelle, setLibelle] = useState('');
  const [nature, setNature] = useState('texte');

  const charger = useCallback(async () => {
    const res = await getChampsCommunsQcm();
    setChamps(res.data || []);
  }, []);

  useEffect(() => {
    charger().catch(() => setChamps([]));
  }, [charger]);

  async function agir(fn) {
    setEnCours(true);
    setErreur(null);
    try {
      await fn();
      await charger();
    } catch (err) {
      setErreur(err.message || 'Action impossible.');
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="qcm-formations">
      <button
        type="button"
        className="qcm-communes-bascule"
        onClick={() => setOuvert((o) => !o)}
        aria-expanded={ouvert}
      >
        <span className="qcm-formations-titre">Questions communes (début de chaque évaluation)</span>
        <span className="qcm-communes-resume">
          {champs.length ? champs.map((c) => c.libelle).join(' · ') : 'aucune'} {ouvert ? '▴' : '▾'}
        </span>
      </button>

      {ouvert && (
        <>
          <div className="qcm-formations-vide">
            Posées avant les questions notées, à chaque passage — elles ne comptent pas dans le score.
          </div>
          <ul className="qcm-communes-liste">
            {champs.map((c) => (
              <li key={c.id} className="qcm-commune">
                <span className="qcm-commune-libelle">{c.libelle}</span>
                <span className="qcm-badge">{NATURES_CHAMP[c.nature] || c.nature}</span>
                <label className="qcm-commune-oblig">
                  <input
                    type="checkbox"
                    checked={c.obligatoire}
                    disabled={enCours}
                    onChange={(e) =>
                      agir(() => updateChampCommunQcm(c.id, { obligatoire: e.target.checked }))
                    }
                  />
                  obligatoire
                </label>
                {c.code !== 'nom_prenom' && (
                  <button
                    type="button"
                    className="qcm-formation-retrait"
                    disabled={enCours}
                    onClick={() => agir(() => deleteChampCommunQcm(c.id))}
                    aria-label={`Retirer ${c.libelle}`}
                    title="Retirer cette question commune"
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
          <RemplirParTexte
            contexte="question commune posée au début de chaque évaluation (libellé et nature : texte ou e-mail)"
            champs={[
              { cle: 'libelle', libelle: 'Libellé de la question commune' },
              {
                cle: 'nature',
                libelle: 'Nature',
                type: 'choix',
                options: [
                  { valeur: 'texte', libelle: 'Texte' },
                  { valeur: 'email', libelle: 'E-mail' },
                ],
              },
            ]}
            onValeurs={(v) => {
              if (v.libelle !== undefined) setLibelle(String(v.libelle));
              if (v.nature !== undefined && NATURES_CHAMP[v.nature]) setNature(v.nature);
            }}
          />
          <form
            className="qcm-formations-ajout"
            onSubmit={(e) => {
              e.preventDefault();
              if (!libelle.trim()) return;
              agir(async () => {
                await createChampCommunQcm({ libelle: libelle.trim(), nature });
                setLibelle('');
                setNature('texte');
              });
            }}
          >
            <input
              type="text"
              className="qcm-commune-champ"
              value={libelle}
              onChange={(e) => setLibelle(e.target.value)}
              placeholder="Ajouter une question commune (ex. Fonction)"
              maxLength={150}
              disabled={enCours}
            />
            <select value={nature} onChange={(e) => setNature(e.target.value)} disabled={enCours} aria-label="Nature">
              <option value="texte">Texte</option>
              <option value="email">E-mail</option>
            </select>
            <button
              type="submit"
              className="devis-btn devis-btn-sm devis-btn-primary"
              disabled={enCours || !libelle.trim()}
            >
              Ajouter
            </button>
          </form>
        </>
      )}
      {erreur && <div className="qcm-formations-erreur">{erreur}</div>}
    </div>
  );
}

/* ================================================================== */
/* Formations du catalogue rattachées à une évaluation                 */
/* ================================================================== */

/**
 * Les formations du catalogue que cette évaluation couvre (migration 097) :
 * des pastilles retirables, et une liste pour en rattacher une de plus.
 * Chaque geste s'enregistre aussitôt.
 */
function FormationsRattachees({ type, onChange }) {
  const [catalogue, setCatalogue] = useState([]);
  // La sélection (plusieurs formations à la fois), le filtre de la liste, et
  // son ouverture.
  const [choix, setChoix] = useState(() => new Set());
  const [filtre, setFiltre] = useState('');
  const [ouvert, setOuvert] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    let vivant = true;
    getFormations({ actif: 'true' })
      .then((res) => vivant && setCatalogue(res.data || []))
      .catch(() => vivant && setCatalogue([]));
    return () => {
      vivant = false;
    };
  }, []);

  const rattachees = type.formations || [];
  const disponibles = catalogue
    .filter((f) => !rattachees.some((r) => r.id === f.id))
    .sort((a, b) => String(a.intitule).localeCompare(String(b.intitule), 'fr'));
  // Le filtre cherche dans l'intitulé et le code, sans accents ni casse.
  const simple = (t) =>
    String(t || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  const visibles = disponibles.filter((f) =>
    simple(`${f.intitule} ${f.code}`).includes(simple(filtre))
  );
  const nbChoisies = disponibles.filter((f) => choix.has(f.id)).length;

  function basculer(id) {
    setChoix((prev) => {
      const suite = new Set(prev);
      if (suite.has(id)) suite.delete(id);
      else suite.add(id);
      return suite;
    });
  }

  async function enregistrer(ids) {
    setEnCours(true);
    setErreur(null);
    try {
      const res = await rattacherFormationsQcm(type.code, ids);
      onChange(res.data);
      setChoix(new Set());
    } catch (err) {
      setErreur(err.message || 'Rattachement impossible.');
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="qcm-formations">
      <div className="qcm-formations-titre">Formations du catalogue rattachées</div>
      {rattachees.length === 0 ? (
        <div className="qcm-formations-vide">Aucune formation rattachée pour l’instant.</div>
      ) : (
        <div className="qcm-formations-liste">
          {rattachees.map((f) => (
            <span className="qcm-formation" key={f.id} title={f.code}>
              {f.intitule}
              <button
                type="button"
                className="qcm-formation-retrait"
                onClick={() => enregistrer(rattachees.filter((r) => r.id !== f.id).map((r) => r.id))}
                disabled={enCours}
                aria-label={`Détacher ${f.intitule}`}
                title="Détacher cette formation"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      {disponibles.length === 0 ? (
        <div className="qcm-formations-vide" style={{ marginTop: 8 }}>
          Toutes les formations du catalogue sont rattachées.
        </div>
      ) : (
        <>
          <div className="qcm-formations-ajout">
            <button
              type="button"
              className="devis-btn devis-btn-sm"
              onClick={() => setOuvert((o) => !o)}
              aria-expanded={ouvert}
            >
              {ouvert ? 'Masquer le catalogue' : 'Rattacher des formations du catalogue…'}
            </button>
            {nbChoisies > 0 && (
              <button
                type="button"
                className="devis-btn devis-btn-sm devis-btn-primary"
                onClick={() =>
                  enregistrer([
                    ...rattachees.map((r) => r.id),
                    ...disponibles.filter((f) => choix.has(f.id)).map((f) => f.id),
                  ])
                }
                disabled={enCours}
              >
                Rattacher la sélection ({nbChoisies})
              </button>
            )}
          </div>

          {ouvert && (
            <div className="qcm-choix">
              <div className="qcm-choix-barre">
                <input
                  type="search"
                  className="qcm-commune-champ"
                  value={filtre}
                  onChange={(e) => setFiltre(e.target.value)}
                  placeholder="Filtrer le catalogue (intitulé ou code)…"
                  aria-label="Filtrer les formations"
                />
                <button
                  type="button"
                  className="devis-btn devis-btn-sm"
                  onClick={() =>
                    setChoix((prev) => new Set([...prev, ...visibles.map((f) => f.id)]))
                  }
                  disabled={!visibles.length}
                >
                  Tout cocher{filtre ? ' (filtrées)' : ''}
                </button>
                <button
                  type="button"
                  className="devis-btn devis-btn-sm"
                  onClick={() => setChoix(new Set())}
                  disabled={!choix.size}
                >
                  Tout décocher
                </button>
              </div>
              <div className="qcm-choix-liste" role="group" aria-label="Formations du catalogue">
                {visibles.length === 0 && (
                  <div className="qcm-formations-vide">Aucune formation ne correspond.</div>
                )}
                {visibles.map((f) => (
                  <label className="qcm-choix-option" key={f.id}>
                    <input
                      type="checkbox"
                      checked={choix.has(f.id)}
                      onChange={() => basculer(f.id)}
                      disabled={enCours}
                    />
                    <span className="qcm-choix-intitule">{f.intitule}</span>
                    <span className="qcm-choix-code">{f.code}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {erreur && <div className="qcm-formations-erreur">{erreur}</div>}
    </div>
  );
}

/* ================================================================== */
/* Questions : versions + CRUD des questions                           */
/* ================================================================== */

function OngletQuestions({ type, libelleType = 'QCM' }) {
  const [versions, setVersions] = useState([]);
  const [versionId, setVersionId] = useState(null);
  const [sections, setSections] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [flash, setFlash] = useState(null);
  // Le panneau replié de gestion des versions, et son champ de création.
  const [gererVersions, setGererVersions] = useState(false);
  const [libelleNouvelle, setLibelleNouvelle] = useState('');
  const [formQuestion, setFormQuestion] = useState(null);
  // Import de questions par copier-coller (l'IA choisit l'axe de chacune).
  const [importOuvert, setImportOuvert] = useState(false);
  // Les questions communes lues dans le formulaire Tally (e-mail, nom…) :
  // ajoutées à la liste commune quand l'import est enregistré.
  const [communesLues, setCommunesLues] = useState([]);
  const [suppression, setSuppression] = useState(null);
  const [enCours, setEnCours] = useState(false);

  const chargerVersions = useCallback(
    async (idSouhaite = null) => {
      const res = await getVersionsQcm(type);
      const liste = res.data || [];
      setVersions(liste);
      setVersionId((courant) => {
        const voulu = idSouhaite ?? courant;
        if (voulu && liste.some((v) => v.id === voulu)) return voulu;
        const active = liste.find((v) => v.actif);
        return (active || liste[0])?.id ?? null;
      });
      return liste;
    },
    [type]
  );

  useEffect(() => {
    let vivant = true;
    setChargement(true);
    Promise.all([chargerVersions(), getSectionsQcm()])
      .then(([, resSections]) => {
        if (vivant) setSections(resSections.data || []);
      })
      .catch((err) => vivant && setErreur(err.message || 'Chargement impossible.'))
      .finally(() => vivant && setChargement(false));
    return () => {
      vivant = false;
    };
  }, [type, chargerVersions]);

  const chargerQuestions = useCallback(async () => {
    if (!versionId) {
      setQuestions([]);
      return;
    }
    const res = await getQuestionsQcm(versionId);
    setQuestions(res.data?.questions || []);
  }, [versionId]);

  useEffect(() => {
    chargerQuestions().catch((err) => setErreur(err.message || 'Questions illisibles.'));
  }, [chargerQuestions]);

  const version = versions.find((v) => v.id === versionId) || null;

  /**
   * « Nouvelle question » ne demande pas de comprendre les versions : s'il
   * n'y en a aucune, la première (« v1 », active) se crée toute seule.
   */
  async function ouvrirNouvelleQuestion() {
    if (version) {
      setFormQuestion({});
      return;
    }
    await action(async () => {
      const res = await createVersionQcm({ typeQcm: type, libelle: 'v1' });
      await updateVersionQcm(res.data.id, { actif: true });
      await chargerVersions(res.data.id);
      setFormQuestion({});
    });
  }

  async function action(fn, message) {
    setEnCours(true);
    setErreur(null);
    try {
      await fn();
      if (message) setFlash(message);
    } catch (err) {
      setErreur(err.message || 'Action impossible.');
    } finally {
      setEnCours(false);
    }
  }

  async function confirmerSuppression() {
    const cible = suppression;
    setSuppression(null);
    if (cible.genre === 'version') {
      await action(async () => {
        await deleteVersionQcm(cible.id);
        await chargerVersions(null);
      }, 'Version supprimée.');
    } else {
      await action(async () => {
        await deleteQuestionQcm(cible.id);
        await chargerQuestions();
      }, 'Question supprimée.');
    }
  }

  // Questions groupées par section, dans l'ordre renvoyé par le serveur.
  const groupes = [];
  for (const q of questions) {
    const dernier = groupes[groupes.length - 1];
    if (dernier && dernier.code === q.section_code) dernier.questions.push(q);
    else
      groupes.push({
        code: q.section_code,
        libelle: q.section_libelle,
        poids: q.section_poids,
        questions: [q],
      });
  }

  return (
    <div className="devis-card qcm-bloc">
      <FlashToast texte={flash} onFermer={() => setFlash(null)} />
      {erreur && <div className="devis-alert devis-alert-erreur">{erreur}</div>}
      {chargement && <div className="devis-state">Chargement…</div>}

      {!chargement && (
        <>
          {/* En tête, les deux gestes qui remplissent la liste. La version
              affichée se choisit à droite, discrètement ; sa gestion (et
              l'explication de ce qu'est une version) est repliée. */}
          <div className="qcm-entre-blocs">
            <div className="qcm-toolbar-actions">
              <button
                type="button"
                className="devis-btn devis-btn-sm"
                onClick={() => setImportOuvert(true)}
                disabled={enCours}
                title="Colle tes questions : l'IA les structure et choisit la section de chacune"
              >
                Importer depuis un texte
              </button>
              <button
                type="button"
                className="devis-btn devis-btn-sm devis-btn-primary"
                onClick={ouvrirNouvelleQuestion}
                disabled={enCours || !sections.length}
                title={
                  sections.length
                    ? 'Ajouter une question'
                    : 'Créez d’abord une section (onglet Sections & pondération)'
                }
              >
                Nouvelle question
              </button>
            </div>

            <div className="qcm-version-zone">
              {versions.length > 1 ? (
                <select
                  className="qcm-version-select"
                  value={versionId || ''}
                  onChange={(e) => setVersionId(Number(e.target.value) || null)}
                  title="La version dont les questions s'affichent"
                >
                  {versions.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.libelle}
                      {v.actif ? ' · active' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                version && (
                  <span className="qcm-note">
                    Version : {version.libelle}
                    {version.actif ? ' · active' : ''}
                  </span>
                )
              )}
              {versions.length > 0 && (
                <button
                  type="button"
                  className="qcm-lien"
                  onClick={() => setGererVersions((v) => !v)}
                >
                  {gererVersions ? 'Fermer' : 'Gérer les versions'}
                </button>
              )}
            </div>
          </div>

          {gererVersions && versions.length > 0 && (
            <div className="qcm-form">
              <div className="qcm-form-titre">Versions</div>
              <p className="qcm-note">
                Une version fige les questions et leur barème. Les candidats passent la
                version <strong>active</strong>, et chaque passage reste noté sur la version
                qu'il a passée. Corriger un test déjà passé : dupliquer la version, modifier
                la copie, puis l'activer.
              </p>
              <ul className="qcm-versions">
                {versions.map((v) => (
                  <li
                    key={v.id}
                    className={
                      'qcm-version-ligne' + (v.id === versionId ? ' qcm-version-courante' : '')
                    }
                  >
                    <button
                      type="button"
                      className="qcm-version-nom"
                      onClick={() => setVersionId(v.id)}
                      title="Afficher les questions de cette version"
                    >
                      {v.libelle}
                    </button>
                    <span className="qcm-version-infos">
                      {v.actif && <span className="qcm-badge qcm-badge-points">active</span>}
                      {v.nb_questions} question{v.nb_questions > 1 ? 's' : ''} · {v.nb_passages}{' '}
                      passage{v.nb_passages > 1 ? 's' : ''}
                    </span>
                    <span className="qcm-toolbar-actions">
                      {!v.actif && (
                        <button
                          type="button"
                          className="devis-btn devis-btn-sm"
                          onClick={() =>
                            action(async () => {
                              await updateVersionQcm(v.id, { actif: true });
                              await chargerVersions(v.id);
                            }, `« ${v.libelle} » est maintenant la version active.`)
                          }
                          disabled={enCours}
                        >
                          Activer
                        </button>
                      )}
                      <button
                        type="button"
                        className="devis-btn devis-btn-sm"
                        onClick={() =>
                          action(async () => {
                            const res = await dupliquerVersionQcm(v.id);
                            await chargerVersions(res.data?.id);
                          }, 'Version dupliquée : vous modifiez la copie.')
                        }
                        disabled={enCours}
                      >
                        Dupliquer
                      </button>
                      <button
                        type="button"
                        className="devis-btn devis-btn-sm devis-btn-danger"
                        onClick={() => setSuppression({ genre: 'version', id: v.id })}
                        disabled={enCours || v.nb_passages > 0}
                        title={
                          v.nb_passages > 0
                            ? 'Des candidats ont passé cette version : elle se conserve'
                            : 'Supprimer la version et ses questions'
                        }
                      >
                        Supprimer
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
              <RemplirParTexte
                contexte="nouvelle version d'une banque de questions QCM (son nom, ex. v2)"
                champs={[{ cle: 'libelle', libelle: 'Nom de la nouvelle version' }]}
                onValeurs={(v) => {
                  if (v.libelle !== undefined) setLibelleNouvelle(String(v.libelle));
                }}
              />
              <form
                className="qcm-ligne-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  action(async () => {
                    const res = await createVersionQcm({ typeQcm: type, libelle: libelleNouvelle });
                    setLibelleNouvelle('');
                    await chargerVersions(res.data?.id);
                  }, 'Version créée.');
                }}
              >
                <input
                  placeholder="Nom de la nouvelle version (ex. v2)"
                  value={libelleNouvelle}
                  onChange={(e) => setLibelleNouvelle(e.target.value)}
                />
                <button
                  type="submit"
                  className="devis-btn devis-btn-sm devis-btn-primary"
                  disabled={enCours || !libelleNouvelle.trim()}
                >
                  Créer la version
                </button>
              </form>
            </div>
          )}

          {version && version.nb_passages > 0 && (
            <p className="qcm-note">
              Des candidats ont passé cette version : pour la corriger, dupliquez-la puis
              modifiez la copie (« Gérer les versions »).
            </p>
          )}

          {!versions.length && (
            <div className="devis-empty">
              <div className="devis-empty-title">Aucune question pour l’instant.</div>
              <div className="devis-empty-subtitle">
                Importez des questions depuis un texte, ou cliquez « Nouvelle question ».
              </div>
            </div>
          )}

          {version && (
            <>
              {formQuestion && (
                <QuestionForm
                  sections={sections}
                  versionId={version.id}
                  initiale={formQuestion.question || null}
                  onFermer={() => setFormQuestion(null)}
                  onEnregistre={async (message) => {
                    setFormQuestion(null);
                    setFlash(message);
                    await chargerQuestions();
                    await chargerVersions(version.id);
                  }}
                />
              )}

              {groupes.map((g) => (
                <div key={g.code} className="qcm-section-bloc">
                  <div className="qcm-section-titre">
                    {g.libelle}
                    <span className="qcm-section-poids">poids {pourcent(g.poids)}</span>
                  </div>
                  {g.questions.map((q) => (
                    <div key={q.id} className="qcm-question">
                      <div className="qcm-question-entete">
                        <span className="qcm-badge">{TYPES_QUESTION[q.type] || q.type}</span>
                        <span className="qcm-badge qcm-badge-points">
                          {q.points_max} pt{q.points_max > 1 ? 's' : ''} max
                        </span>
                        {q.temps_limite_s && (
                          <span className="qcm-badge">{q.temps_limite_s} s</span>
                        )}
                        <span className="qcm-question-actions">
                          <button
                            type="button"
                            className="devis-btn devis-btn-sm"
                            onClick={() => setFormQuestion({ question: q })}
                          >
                            Modifier
                          </button>
                          <button
                            type="button"
                            className="devis-btn devis-btn-sm devis-btn-danger"
                            onClick={() => setSuppression({ genre: 'question', id: q.id })}
                          >
                            Supprimer
                          </button>
                        </span>
                      </div>
                      <div className="qcm-question-enonce">{q.enonce}</div>
                      <ul className="qcm-options">
                        {q.options.map((o) => (
                          <li
                            key={o.id}
                            className={'qcm-option' + (o.est_correcte ? ' qcm-option-bonne' : '')}
                          >
                            <span className="qcm-option-points">
                              {o.points} pt{o.points > 1 ? 's' : ''}
                            </span>
                            <span>{o.libelle}</span>
                            {q.type === 'qcm_multiple' && o.est_correcte && (
                              <span className="qcm-option-coche" title="Option à cocher">
                                ✓
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ))}

              {!questions.length && (
                <div className="devis-empty">
                  <div className="devis-empty-title">Aucune question dans cette version.</div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {importOuvert && (
        <ImportTexteModal
          titre={`Importer des questions — ${libelleType}`}
          aide="Collez vos questions (énoncés, options, barème s'il existe). L'IA structure chaque question et choisit elle-même sa section : les axes manquants sont créés à l'enregistrement. Sans version sélectionnée, une version neuve reçoit l'import."
          exemple={
            'Exemple :\nUn collègue vous demande de l’aide alors que votre tâche est en retard. Que faites-vous ?\na) J’explique ma charge et je propose un ordre de passage (3 pts)\nb) Je termine ma tâche d’abord (2 pts)\nc) J’obéis sans un mot (1 pt)\nd) Je refuse (0 pt)\n…'
          }
          motPluriel="questions"
          cleObjet="question"
          lienSource={{
            libelle: 'Lire le formulaire',
            placeholder: 'Lien du formulaire Tally — https://tally.so/r/…',
            aide: 'Un formulaire Tally public : ses questions à choix sont lues, et le barème s’il s’agit d’un quiz. L’IA choisit ensuite l’axe de chaque question ; vous relisez avant d’enregistrer.',
            charger: async (lien) => {
              const res = await lireFormulaireTally(lien);
              setCommunesLues(res.data?.communes || []);
              return res;
            },
          }}
          colonnes={COLONNES_IMPORT_QCM}
          analyser={(texte) => analyserImportQcm({ typeQcm: type, texte }).then((res) => res.data)}
          enregistrer={(retenues) =>
            enregistrerImportQcm({
              typeQcm: type,
              versionId: versionId || null,
              lignes: retenues.map((p) => ({ question: p.question })),
              communes: communesLues,
            }).then((res) => res.data)
          }
          onFermer={() => setImportOuvert(false)}
          onEnregistre={async (bilan) => {
            if (!bilan.creees) return;
            setFlash(
              `${bilan.creees} question${bilan.creees > 1 ? 's' : ''} importée${bilan.creees > 1 ? 's' : ''}` +
                (bilan.sectionsCreees
                  ? `, ${bilan.sectionsCreees} axe${bilan.sectionsCreees > 1 ? 's' : ''} créé${bilan.sectionsCreees > 1 ? 's' : ''} (poids à régler)`
                  : '') +
                (bilan.communesAjoutees?.length
                  ? `, question${bilan.communesAjoutees.length > 1 ? 's' : ''} commune${bilan.communesAjoutees.length > 1 ? 's' : ''} ajoutée${bilan.communesAjoutees.length > 1 ? 's' : ''} : ${bilan.communesAjoutees.join(', ')}`
                  : '') +
                '.'
            );
            try {
              const resSections = await getSectionsQcm();
              setSections(resSections.data || []);
              await chargerVersions(bilan.versionId);
              await chargerQuestions();
            } catch {
              /* la liste se rattrapera au prochain chargement */
            }
          }}
        />
      )}

      {suppression && (
        <ConfirmationSuppression
          titre={
            suppression.genre === 'version' ? 'Supprimer cette version ?' : 'Supprimer cette question ?'
          }
          sousTitre={
            suppression.genre === 'version'
              ? versions.find((v) => v.id === suppression.id)?.libelle
              : 'Son barème et ses options partent avec elle.'
          }
          details={
            suppression.genre === 'version'
              ? ['Toutes ses questions et leurs barèmes.']
              : ['La question et ses options.']
          }
          note="Impossible dès qu'un candidat y a répondu : les anciens scores doivent rester explicables."
          enCours={enCours}
          onAnnuler={() => setSuppression(null)}
          onConfirmer={confirmerSuppression}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Le formulaire d'une question : énoncé + barème gradué des options   */
/* ------------------------------------------------------------------ */

const OPTION_VIDE = { libelle: '', points: 0, estCorrecte: false };

function QuestionForm({ sections, versionId, initiale, onFermer, onEnregistre }) {
  const [valeurs, setValeurs] = useState(() =>
    initiale
      ? {
          sectionCode: initiale.section_code,
          type: initiale.type,
          enonce: initiale.enonce,
          tempsLimiteS: initiale.temps_limite_s || '',
          ordre: initiale.ordre || 0,
          options: initiale.options.map((o) => ({
            libelle: o.libelle,
            points: o.points,
            estCorrecte: o.est_correcte,
          })),
        }
      : {
          sectionCode: sections[0]?.code || '',
          type: 'qcm',
          enonce: '',
          tempsLimiteS: '',
          ordre: 0,
          options: [{ ...OPTION_VIDE }, { ...OPTION_VIDE }],
        }
  );
  const [erreur, setErreur] = useState(null);
  const [enregistrement, setEnregistrement] = useState(false);

  const poser = (champ, valeur) => setValeurs((v) => ({ ...v, [champ]: valeur }));
  const poserOption = (i, champ, valeur) =>
    setValeurs((v) => ({
      ...v,
      options: v.options.map((o, j) => (j === i ? { ...o, [champ]: valeur } : o)),
    }));

  async function enregistrer(e) {
    e.preventDefault();
    setEnregistrement(true);
    setErreur(null);
    const corps = {
      versionId,
      sectionCode: valeurs.sectionCode,
      type: valeurs.type,
      enonce: valeurs.enonce,
      tempsLimiteS: valeurs.tempsLimiteS === '' ? null : Number(valeurs.tempsLimiteS),
      ordre: Number(valeurs.ordre) || 0,
      options: valeurs.options
        .filter((o) => o.libelle.trim())
        .map((o, i) => ({
          libelle: o.libelle,
          points: Number(o.points) || 0,
          estCorrecte: o.estCorrecte,
          ordre: i,
        })),
    };
    try {
      if (initiale) {
        await updateQuestionQcm(initiale.id, corps);
        await onEnregistre('Question mise à jour.');
      } else {
        await createQuestionQcm(corps);
        await onEnregistre('Question créée.');
      }
    } catch (err) {
      setErreur(err.message || 'Enregistrement impossible.');
      setEnregistrement(false);
    }
  }

  return (
    <form className="qcm-form" onSubmit={enregistrer}>
      <div className="qcm-form-titre">
        {initiale ? 'Modifier la question' : 'Nouvelle question'}
      </div>

      {/* Champs simples seulement : les options de réponse (liste dynamique) restent saisies à la main. */}
      <RemplirParTexte
        contexte="question de QCM (énoncé, section, type de réponse, temps limite, ordre)"
        champs={[
          {
            cle: 'sectionCode',
            libelle: 'Section',
            type: 'choix',
            options: sections.map((s) => ({ valeur: s.code, libelle: s.libelle })),
          },
          {
            cle: 'type',
            libelle: 'Type',
            type: 'choix',
            options: [
              { valeur: 'qcm', libelle: 'Choix unique' },
              { valeur: 'qcm_multiple', libelle: 'Choix multiple' },
            ],
          },
          { cle: 'tempsLimiteS', libelle: 'Temps limite (s)', type: 'nombre' },
          { cle: 'ordre', libelle: 'Ordre', type: 'nombre' },
          { cle: 'enonce', libelle: 'Énoncé' },
        ]}
        onValeurs={(v) =>
          setValeurs((prev) => {
            const suite = { ...prev };
            // Les champs numériques de l'état sont des chaînes (<input type="number">).
            if (v.sectionCode !== undefined && sections.some((s) => s.code === v.sectionCode))
              suite.sectionCode = v.sectionCode;
            if (v.type !== undefined && TYPES_QUESTION[v.type]) suite.type = v.type;
            if (v.tempsLimiteS !== undefined) suite.tempsLimiteS = String(v.tempsLimiteS);
            if (v.ordre !== undefined) suite.ordre = String(v.ordre);
            if (v.enonce !== undefined) suite.enonce = String(v.enonce);
            return suite;
          })
        }
      />

      <div className="qcm-form-ligne">
        <label className="devis-form-field">
          <span>Section</span>
          <select
            value={valeurs.sectionCode}
            onChange={(e) => poser('sectionCode', e.target.value)}
          >
            {sections.map((s) => (
              <option key={s.code} value={s.code}>
                {s.libelle}
              </option>
            ))}
          </select>
        </label>
        <label className="devis-form-field">
          <span>Type</span>
          <select value={valeurs.type} onChange={(e) => poser('type', e.target.value)}>
            <option value="qcm">Choix unique</option>
            <option value="qcm_multiple">Choix multiple</option>
          </select>
        </label>
        <label className="devis-form-field">
          <span>Temps limite (s, facultatif)</span>
          <input
            type="number"
            min="5"
            value={valeurs.tempsLimiteS}
            onChange={(e) => poser('tempsLimiteS', e.target.value)}
            placeholder="aucun"
          />
        </label>
        <label className="devis-form-field">
          <span>Ordre</span>
          <input
            type="number"
            value={valeurs.ordre}
            onChange={(e) => poser('ordre', e.target.value)}
          />
        </label>
      </div>

      <label className="devis-form-field">
        <span>Énoncé</span>
        <textarea
          rows={3}
          value={valeurs.enonce}
          onChange={(e) => poser('enonce', e.target.value)}
          placeholder="Ex. Un collègue vous demande de l'aide alors que votre tâche est en retard…"
          required
        />
      </label>

      <div className="qcm-form-options-titre">
        Options et barème
        <span className="qcm-note">
          {valeurs.type === 'qcm'
            ? ' — la meilleure option fixe le plafond.'
            : ' — cochez les bonnes options ; une mauvaise cochée retire 1 point.'}
        </span>
      </div>

      {valeurs.options.map((o, i) => (
        <div key={i} className="qcm-form-option">
          <input
            className="qcm-form-option-libelle"
            placeholder={`Option ${i + 1}`}
            value={o.libelle}
            onChange={(e) => poserOption(i, 'libelle', e.target.value)}
          />
          <input
            className="qcm-form-option-points"
            type="number"
            min="0"
            max="100"
            value={o.points}
            onChange={(e) => poserOption(i, 'points', e.target.value)}
            title="Points de cette option"
          />
          {valeurs.type === 'qcm_multiple' && (
            <label className="qcm-form-option-correcte" title="Option à cocher par le candidat">
              <input
                type="checkbox"
                checked={o.estCorrecte}
                onChange={(e) => poserOption(i, 'estCorrecte', e.target.checked)}
              />
              bonne
            </label>
          )}
          <button
            type="button"
            className="devis-btn devis-btn-sm"
            onClick={() =>
              setValeurs((v) => ({ ...v, options: v.options.filter((_, j) => j !== i) }))
            }
            disabled={valeurs.options.length <= 2}
            aria-label={`Retirer l'option ${i + 1}`}
          >
            ×
          </button>
        </div>
      ))}

      <button
        type="button"
        className="devis-btn devis-btn-sm"
        onClick={() => setValeurs((v) => ({ ...v, options: [...v.options, { ...OPTION_VIDE }] }))}
      >
        Ajouter une option
      </button>

      {erreur && <div className="devis-alert devis-alert-erreur">{erreur}</div>}

      <div className="qcm-form-actions">
        <button type="button" className="devis-btn devis-btn-sm" onClick={onFermer}>
          Annuler
        </button>
        <button
          type="submit"
          className="devis-btn devis-btn-sm devis-btn-primary"
          disabled={enregistrement}
        >
          {enregistrement ? 'Enregistrement' : 'Enregistrer'}
        </button>
      </div>
    </form>
  );
}

/* ================================================================== */
/* Sections : les axes et leur pondération                             */
/* ================================================================== */

const SECTION_VIDE = { libelle: '', poidsPourcent: '', ordre: 0 };

/* Les sections et leurs poids sont COMMUNS aux QCM clients et formateurs ;
   le code d'une section est dérivé du libellé par le serveur, rien à saisir. */
function OngletSections() {
  const [sections, setSections] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [flash, setFlash] = useState(null);
  // null : formulaire fermé ; { codeInitial } : édition ; sinon création.
  const [form, setForm] = useState(null);
  const [suppression, setSuppression] = useState(null);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(async () => {
    const res = await getSectionsQcm();
    setSections(res.data || []);
  }, []);

  useEffect(() => {
    let vivant = true;
    setChargement(true);
    charger()
      .catch((err) => vivant && setErreur(err.message || 'Chargement impossible.'))
      .finally(() => vivant && setChargement(false));
    return () => {
      vivant = false;
    };
  }, [charger]);

  const sommePoids = sections.reduce((s, x) => s + Number(x.poids_defaut), 0);

  async function enregistrer(e) {
    e.preventDefault();
    setEnCours(true);
    setErreur(null);
    const corps = {
      libelle: form.libelle,
      poidsDefaut: (Number(form.poidsPourcent) || 0) / 100,
      ordre: Number(form.ordre) || 0,
    };
    try {
      if (form.codeInitial) {
        await updateSectionQcm(form.codeInitial, corps);
        setFlash('Section mise à jour.');
      } else {
        await createSectionQcm(corps);
        setFlash('Section créée.');
      }
      setForm(null);
      await charger();
    } catch (err) {
      setErreur(err.message || 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  }

  async function confirmerSuppression() {
    const cible = suppression;
    setSuppression(null);
    setEnCours(true);
    setErreur(null);
    try {
      await deleteSectionQcm(cible.code);
      setFlash('Section supprimée.');
      await charger();
    } catch (err) {
      setErreur(err.message || 'Suppression impossible.');
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="devis-card qcm-bloc">
      <FlashToast texte={flash} onFermer={() => setFlash(null)} />
      {erreur && <div className="devis-alert devis-alert-erreur">{erreur}</div>}
      {chargement && <div className="devis-state">Chargement…</div>}

      {!chargement && (
        <>
          <div className="qcm-entre-blocs">
            <span className="qcm-note">
              Communes aux QCM clients et formateurs · Somme des poids :{' '}
              <strong>{pourcent(sommePoids)}</strong>
              {Math.abs(sommePoids - 1) > 0.001 && ' (visez 100 %)'}
            </span>
            <button
              type="button"
              className="devis-btn devis-btn-sm devis-btn-primary"
              onClick={() => setForm({ ...SECTION_VIDE })}
            >
              Nouvelle section
            </button>
          </div>

          {form && (
            <form className="qcm-form" onSubmit={enregistrer}>
              <div className="qcm-form-titre">
                {form.codeInitial ? `Modifier « ${form.libelle} »` : 'Nouvelle section'}
              </div>
              <RemplirParTexte
                contexte="section (axe) d'un QCM : libellé, poids en pourcentage de la note, ordre d'affichage"
                champs={[
                  { cle: 'libelle', libelle: 'Libellé' },
                  { cle: 'poidsPourcent', libelle: 'Poids (%)', type: 'nombre' },
                  { cle: 'ordre', libelle: 'Ordre', type: 'nombre' },
                ]}
                onValeurs={(v) =>
                  setForm((f) => ({
                    ...f,
                    ...(v.libelle !== undefined && { libelle: String(v.libelle) }),
                    // Chaînes : ce sont des <input type="number">.
                    ...(v.poidsPourcent !== undefined && { poidsPourcent: String(v.poidsPourcent) }),
                    ...(v.ordre !== undefined && { ordre: String(v.ordre) }),
                  }))
                }
              />
              <div className="qcm-form-ligne">
                <label className="devis-form-field">
                  <span>Libellé</span>
                  <input
                    value={form.libelle}
                    onChange={(e) => setForm((f) => ({ ...f, libelle: e.target.value }))}
                    placeholder="ex. Logique"
                    required
                  />
                </label>
                <label className="devis-form-field">
                  <span>Poids (%)</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={form.poidsPourcent}
                    onChange={(e) => setForm((f) => ({ ...f, poidsPourcent: e.target.value }))}
                    placeholder="15"
                  />
                </label>
                <label className="devis-form-field">
                  <span>Ordre</span>
                  <input
                    type="number"
                    value={form.ordre}
                    onChange={(e) => setForm((f) => ({ ...f, ordre: e.target.value }))}
                  />
                </label>
              </div>
              <div className="qcm-form-actions">
                <button type="button" className="devis-btn devis-btn-sm" onClick={() => setForm(null)}>
                  Annuler
                </button>
                <button
                  type="submit"
                  className="devis-btn devis-btn-sm devis-btn-primary"
                  disabled={enCours}
                >
                  {enCours ? 'Enregistrement' : 'Enregistrer'}
                </button>
              </div>
            </form>
          )}

          {sections.length > 0 ? (
            <table className="devis-table">
              <thead>
                <tr>
                  <th>SECTION</th>
                  <th>POIDS</th>
                  <th>QUESTIONS</th>
                  <th className="devis-th-actions">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {sections.map((s) => (
                  <tr key={s.code} className="devis-row">
                    <td>
                      <div className="devis-numero">{s.libelle}</div>
                      <div className="devis-contact-email">{s.code}</div>
                    </td>
                    <td>{pourcent(s.poids_defaut)}</td>
                    <td>
                      {s.nb_questions} question{s.nb_questions > 1 ? 's' : ''}
                    </td>
                    <td>
                      <div className="devis-actions-pile">
                        <button
                          type="button"
                          className="devis-btn devis-btn-sm"
                          onClick={() =>
                            setForm({
                              codeInitial: s.code,
                              libelle: s.libelle,
                              poidsPourcent: Math.round(Number(s.poids_defaut) * 1000) / 10,
                              ordre: s.ordre,
                            })
                          }
                        >
                          Modifier
                        </button>
                        <button
                          type="button"
                          className="devis-btn devis-btn-sm devis-btn-danger"
                          onClick={() => setSuppression(s)}
                          disabled={s.nb_questions > 0}
                          title={
                            s.nb_questions > 0
                              ? 'Des questions portent cette section : déplacez-les d’abord'
                              : 'Supprimer la section'
                          }
                        >
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="devis-empty">
              <div className="devis-empty-title">Aucune section.</div>
              <div className="devis-empty-subtitle">
                Les sections sont les axes de notation (rédaction, logique…).
              </div>
            </div>
          )}
        </>
      )}

      {suppression && (
        <ConfirmationSuppression
          titre="Supprimer cette section ?"
          sousTitre={`${suppression.libelle} (${suppression.code})`}
          details={['L’axe et son poids dans le score global.']}
          note="Impossible si des questions ou des scores s'y rattachent."
          enCours={enCours}
          onAnnuler={() => setSuppression(null)}
          onConfirmer={confirmerSuppression}
        />
      )}
    </div>
  );
}

/* ================================================================== */
/* Passages : les résultats, score global, détail par axe              */
/* ================================================================== */

function OngletPassages({ type }) {
  const [passages, setPassages] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [flash, setFlash] = useState(null);
  const [detail, setDetail] = useState(null);
  const [suppression, setSuppression] = useState(null);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(async () => {
    const res = await getPassagesQcm({ type });
    setPassages(res.data || []);
  }, [type]);

  useEffect(() => {
    let vivant = true;
    setChargement(true);
    charger()
      .catch((err) => vivant && setErreur(err.message || 'Chargement impossible.'))
      .finally(() => vivant && setChargement(false));
    return () => {
      vivant = false;
    };
  }, [charger]);

  async function ouvrirDetail(id) {
    setErreur(null);
    try {
      const res = await getPassageQcm(id);
      setDetail(res.data);
    } catch (err) {
      setErreur(err.message || 'Détail illisible.');
    }
  }

  async function confirmerSuppression() {
    const cible = suppression;
    setSuppression(null);
    setEnCours(true);
    try {
      await deletePassageQcm(cible.id);
      setFlash('Passage supprimé.');
      if (detail?.id === cible.id) setDetail(null);
      await charger();
    } catch (err) {
      setErreur(err.message || 'Suppression impossible.');
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="devis-card qcm-bloc">
      <FlashToast texte={flash} onFermer={() => setFlash(null)} />
      {erreur && <div className="devis-alert devis-alert-erreur">{erreur}</div>}
      {chargement && <div className="devis-state">Chargement…</div>}

      {!chargement && passages.length === 0 && (
        <div className="devis-empty">
          <div className="devis-empty-title">Aucun passage pour cette banque.</div>
          <div className="devis-empty-subtitle">
            Les résultats apparaissent ici une fois les tests soumis.
          </div>
        </div>
      )}

      {!chargement && passages.length > 0 && (
        <table className="devis-table">
          <thead>
            <tr>
              <th>CANDIDAT</th>
              <th>VERSION</th>
              <th>PASSÉ LE</th>
              <th>RÉSULTAT</th>
              <th className="devis-th-actions">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {passages.map((p) => (
              <tr key={p.id} className="devis-row" onClick={() => ouvrirDetail(p.id)}>
                <td>
                  <div className="devis-contact-name">{p.participant}</div>
                  {p.email && <div className="devis-contact-email">{p.email}</div>}
                  {p.session_intitule && (
                    <div className="devis-contact-email">{p.session_intitule}</div>
                  )}
                  {/* Les autres questions communes : formateur, entreprise… */}
                  {p.infos && Object.keys(p.infos).length > 0 && (
                    <div className="devis-contact-email">{Object.values(p.infos).join(' · ')}</div>
                  )}
                </td>
                <td>{p.version_libelle}</td>
                <td>{p.termine_le ? formatDateTime(p.termine_le) : '—'}</td>
                <td>
                  {/* La note sur le nombre de questions, et le pourcentage de
                      réussite ; le verdict par seuils en dessous. */}
                  {p.nb_questions != null ? (
                    <div className="qcm-resultat">
                      <span className="qcm-resultat-note">
                        {p.nb_reussies} / {p.nb_questions}
                      </span>
                      <span className="qcm-resultat-pct">
                        {Math.round(Number(p.pourcentage_reussite))} % de réussite
                      </span>
                    </div>
                  ) : null}
                  {p.score_global != null ? (
                    <span className={`qcm-statut qcm-statut-${p.statut_code || 'inconnu'}`}>
                      {p.nb_questions != null ? '' : `${Math.round(Number(p.score_global))} % · `}
                      {p.statut_libelle || '—'}
                    </span>
                  ) : p.nb_questions == null ? (
                    '—'
                  ) : null}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="devis-actions-pile">
                    <button
                      type="button"
                      className="devis-btn devis-btn-sm devis-btn-fichier"
                      onClick={() => ouvrirDetail(p.id)}
                    >
                      Détail
                    </button>
                    <button
                      type="button"
                      className="devis-btn devis-btn-sm devis-btn-danger"
                      onClick={() => setSuppression(p)}
                    >
                      Supprimer
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {detail && (
        <PanneauApercu
          titre={detail.participant}
          sousTitre={`${detail.version_libelle} · ${
            detail.termine_le ? formatDateTime(detail.termine_le) : ''
          }`}
          onClose={() => setDetail(null)}
        >
          <div className="devis-modal-body">
            {/* Le résultat de la personne : sa note sur le nombre de questions et son
                pourcentage de réussite, puis le verdict par seuils. */}
            {detail.nb_questions != null && (
              <div className="qcm-tuiles">
                <div className="qcm-tuile">
                  <div className="qcm-tuile-valeur">
                    {detail.nb_reussies} / {detail.nb_questions}
                  </div>
                  <div className="qcm-tuile-libelle">questions réussies</div>
                </div>
                <div className="qcm-tuile">
                  <div className="qcm-tuile-valeur">
                    {Math.round(Number(detail.pourcentage_reussite))} %
                  </div>
                  <div className="qcm-tuile-libelle">de réussite</div>
                  <div className="qcm-tuile-detail">
                    {detail.points_obtenus} / {detail.points_max} points
                  </div>
                </div>
              </div>
            )}
            <div className="qcm-detail-score">
              <span className={`qcm-statut qcm-statut-${detail.statut_code || 'inconnu'}`}>
                {detail.score_global != null ? `Score pondéré ${Math.round(Number(detail.score_global))} %` : '—'}{' '}
                · {detail.statut_libelle || 'sans statut'}
              </span>
            </div>

            <div className="qcm-form-titre">Scores par section</div>
            <table className="devis-table">
              <thead>
                <tr>
                  <th>SECTION</th>
                  <th>SCORE</th>
                  <th>POIDS APPLIQUÉ</th>
                </tr>
              </thead>
              <tbody>
                {(detail.scores || []).map((s) => (
                  <tr key={s.section_code}>
                    <td>{s.section_libelle}</td>
                    <td>{Math.round(Number(s.score))} %</td>
                    <td>{pourcent(s.poids_applique)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="qcm-form-titre" style={{ marginTop: 16 }}>
              Réponses ({(detail.reponses || []).length} option
              {(detail.reponses || []).length > 1 ? 's' : ''} cochée
              {(detail.reponses || []).length > 1 ? 's' : ''})
            </div>
            <ul className="qcm-options">
              {(detail.reponses || []).map((r) => (
                <li key={`${r.question_id}-${r.option_id}`} className="qcm-option">
                  <span
                    className={
                      'qcm-option-points' + (r.points_obtenus < 0 ? ' qcm-option-penalite' : '')
                    }
                  >
                    {r.points_obtenus >= 0 ? `+${r.points_obtenus}` : r.points_obtenus} pt
                  </span>
                  <span>
                    <span className="qcm-detail-enonce">{r.enonce}</span>
                    <br />
                    {r.option_libelle}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </PanneauApercu>
      )}

      {suppression && (
        <ConfirmationSuppression
          titre="Supprimer ce passage ?"
          sousTitre={suppression.participant}
          details={['Le passage, ses réponses et ses scores.']}
          enCours={enCours}
          onAnnuler={() => setSuppression(null)}
          onConfirmer={confirmerSuppression}
        />
      )}
    </div>
  );
}

export default Qcm;
